// Dashboard showcase — stripped-down JSX transpositions of the live widgets
// (same treatment as Backtest Lab / HMM regime demos), replacing the previous
// frozen JPG captures. Slide deck chrome preserved: auto-advance, clickable
// dots, reduced-motion respected, SAMPLE badge + caption discipline.
import { useEffect, useState } from "react";

const MONO = "'JetBrains Mono', monospace";
const GREEN = "#00c896", RED = "#f87171", AMBER = "#fbbf24";

const Row = ({ children, C, last }) => (
  <div className="grid items-center px-3 py-1.5 text-[10px]"
    style={{ gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))", borderBottom: last ? "none" : `1px solid ${C.border}` }}>
    {children}
  </div>
);

/* 1 — Portfolio Risk: VaR block + scenario shocks */
const RiskSlide = ({ C }) => (
  <div className="p-3">
    <div className="grid grid-cols-4 gap-2 mb-3">
      {[["VaR 95%", "−$4,310", RED], ["CVaR 95%", "−$6,905", RED], ["Breach P", "3.1%", GREEN], ["Portfolio Vol", "21.4%", C.text]].map(([l, v, c]) => (
        <div key={l} className="rounded-md px-2 py-1.5" style={{ background: C.cardAlt, border: `1px solid ${C.border}` }}>
          <div className="text-[8px]" style={{ color: "#555" }}>{l}</div>
          <div className="text-[11px] font-semibold" style={{ color: c, fontFamily: MONO }}>{v}</div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-4 px-3 pb-1 text-[9px] font-medium" style={{ color: "#555" }}>
      <span>Scenario</span><span className="text-right">Shock</span><span className="text-right">Est. loss</span><span className="text-right">Floor</span>
    </div>
    {[["1σ (21d)", "−4.9%", "−$5,010", "OK", GREEN], ["3σ (21d)", "−14.6%", "−$14,930", "OK", GREEN],
      ["COVID-like", "−34%", "−$34,780", "OK", GREEN], ["GFC-like", "−55%", "−$56,260", "BREACH", RED]].map(([s, sh, l, f, fc], i) => (
      <div key={s} className="grid grid-cols-4 px-3 py-1.5 text-[10px]" style={{ borderTop: `1px solid ${C.border}` }}>
        <span style={{ color: C.muted }}>{s}</span>
        <span className="text-right" style={{ color: C.muted, fontFamily: MONO }}>{sh}</span>
        <span className="text-right" style={{ color: RED, fontFamily: MONO }}>{l}</span>
        <span className="text-right font-bold" style={{ color: fc, fontFamily: MONO }}>{f}</span>
      </div>
    ))}
  </div>
);

/* 2 — Portfolio Heatmap: exposure tiles vs risk capacity */
const HeatSlide = ({ C }) => {
  const tiles = [
    { t: "BA", pnl: "+6.2%", w: 30, bg: "rgba(0,200,150,0.22)" },
    { t: "WFC", pnl: "−1.8%", w: 22, bg: "rgba(248,113,113,0.20)" },
    { t: "AMZN", pnl: "+3.4%", w: 16, bg: "rgba(0,200,150,0.16)" },
    { t: "AAPL", pnl: "+0.9%", w: 12, bg: "rgba(0,200,150,0.10)" },
    { t: "CSP · PLTR", pnl: "contingent", w: 10, bg: "repeating-linear-gradient(45deg, rgba(251,191,36,0.16), rgba(251,191,36,0.16) 4px, transparent 4px, transparent 8px)" },
  ];
  return (
    <div className="p-3">
      <div className="flex gap-1 rounded-md overflow-hidden" style={{ height: 120, border: `1px solid ${C.border}` }}>
        {tiles.map((x) => (
          <div key={x.t} className="flex flex-col items-center justify-center px-1 text-center"
            style={{ width: `${x.w}%`, background: x.bg, borderRight: `1px solid ${C.border}` }}>
            <span className="text-[10px] font-bold" style={{ color: C.text, fontFamily: MONO }}>{x.t}</span>
            <span className="text-[8px]" style={{ color: C.muted, fontFamily: MONO }}>{x.pnl}</span>
          </div>
        ))}
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[8px]" style={{ color: "#555", fontFamily: MONO }}>spare capacity</span>
        </div>
      </div>
      <div className="flex justify-between mt-2 text-[9px]" style={{ color: "#555" }}>
        <span>Canvas = your exposure ceiling under your own Settings limits</span>
        <span style={{ fontFamily: MONO }}>margin cap 50% · single position 10%</span>
      </div>
    </div>
  );
};

/* 3 — Wheel Income: trade-log analytics strip + cumulative P&L line */
const WheelSlide = ({ C }) => (
  <div className="p-3">
    <div className="grid grid-cols-4 gap-2 mb-2">
      {[["Win rate", "78%"], ["Total P&L", "+$24,380"], ["Profit factor", "2.4"], ["Avg credit", "$312"]].map(([l, v]) => (
        <div key={l} className="rounded-md px-2 py-1.5" style={{ background: C.cardAlt, border: `1px solid ${C.border}` }}>
          <div className="text-[8px]" style={{ color: "#555" }}>{l}</div>
          <div className="text-[11px] font-semibold" style={{ color: l === "Total P&L" ? GREEN : C.text, fontFamily: MONO }}>{v}</div>
        </div>
      ))}
    </div>
    <svg viewBox="0 0 300 70" className="w-full" style={{ height: 84 }} aria-hidden="true">
      <polyline fill="none" stroke={C.border} strokeWidth="0.5" points="0,60 300,60" />
      <polyline fill="rgba(0,200,150,0.08)" stroke="none"
        points="0,60 0,58 30,55 60,50 90,52 120,44 150,40 180,34 210,30 240,24 270,18 300,14 300,60" />
      <polyline fill="none" stroke={GREEN} strokeWidth="1.5"
        points="0,58 30,55 60,50 90,52 120,44 150,40 180,34 210,30 240,24 270,18 300,14" />
    </svg>
    <div className="text-[9px]" style={{ color: "#555" }}>Cumulative realized P&L — closed wheel-family premium blocks, netted.</div>
  </div>
);

/* 4 — Position Management: rule-based flags */
const PmSlide = ({ C }) => {
  const rows = [
    ["BA", "Bull", "+6.2%", "TRIM", AMBER, "2 rules", true],
    ["APP", "Vol", "+41.7%", "HEDGE", "#4fc3f7", "1 rule", true],
    ["WFC", "Quiet", "−1.8%", "HOLD", GREEN, "0 rules", false],
    ["AMZN", "Bull", "+3.4%", "HOLD", GREEN, "0 rules", false],
  ];
  return (
    <div className="p-3">
      <div className="grid grid-cols-6 px-2 pb-1 text-[9px] font-medium" style={{ color: "#555" }}>
        <span>Ticker</span><span>Regime</span><span className="text-right">P&L</span><span className="text-center">Assessment</span><span className="text-center">Rules</span><span className="text-center">CC</span>
      </div>
      {rows.map(([t, r, p, a, ac, ru, cc]) => (
        <div key={t} className="grid grid-cols-6 items-center px-2 py-1.5 text-[10px]" style={{ borderTop: `1px solid ${C.border}` }}>
          <span className="font-bold" style={{ color: C.text, fontFamily: MONO }}>{t}</span>
          <span style={{ color: C.muted }}>{r}</span>
          <span className="text-right" style={{ color: p.startsWith("+") ? GREEN : RED, fontFamily: MONO }}>{p}</span>
          <span className="text-center"><span className="px-1.5 py-0.5 rounded text-[8px] font-bold" style={{ color: ac, border: `1px solid ${ac}44` }}>{a}</span></span>
          <span className="text-center" style={{ color: C.muted, fontFamily: MONO }}>{ru}</span>
          <span className="text-center">{cc ? <span className="px-1 py-0.5 rounded text-[8px] font-bold" style={{ color: GREEN, border: `1px solid ${GREEN}44` }}>● CC</span> : <span style={{ color: "#333" }}>—</span>}</span>
        </div>
      ))}
      <div className="mt-2 text-[9px]" style={{ color: "#555" }}>Flags restate your Settings rules — informational, not advice. ● CC = covered-call conditions align.</div>
    </div>
  );
};

/* 5 — Hedging: put coverage & protected floors */
const HedgeSlide = ({ C }) => (
  <div className="p-3">
    {[["AMZN", 100, "floor $250 · through Sep 04", GREEN], ["APP", 60, "3 of 5 lots covered", AMBER],
      ["BA", 0, "no protective put — 38% of book", RED], ["WFC", 0, "no protective put — 20% of book", RED]].map(([t, w, d, c]) => (
      <div key={t} className="mb-2.5">
        <div className="flex justify-between text-[10px] mb-1">
          <span className="font-bold" style={{ color: C.text, fontFamily: MONO }}>{t}</span>
          <span style={{ color: C.muted }}>{d}</span>
          <span style={{ color: c, fontFamily: MONO }}>{w}%</span>
        </div>
        <div className="h-1.5 rounded" style={{ background: C.border }}>
          <div className="h-1.5 rounded" style={{ width: `${Math.max(w, 2)}%`, background: c }} />
        </div>
      </div>
    ))}
    <div className="text-[9px]" style={{ color: "#555" }}>Coverage = live long puts matched to held shares, per your hedge rules.</div>
  </div>
);

const SLIDES = [
  { key: "risk", label: "Portfolio Risk — VaR / CVaR & scenario shocks", Comp: RiskSlide },
  { key: "heatmap", label: "Portfolio Heatmap — exposure vs your risk capacity", Comp: HeatSlide },
  { key: "wheel", label: "Wheel Income — Trade Log analytics", Comp: WheelSlide },
  { key: "pm", label: "Position Management — rule-based flags", Comp: PmSlide },
  { key: "hedging", label: "Hedging — put coverage & protected floors", Comp: HedgeSlide },
];

export const DashboardShowcase = ({ C }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div className="rounded-lg overflow-hidden flex flex-col" style={{ background: C.card, border: `1px solid ${C.border}` }} data-testid="showcase-dashboard">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between flex-wrap gap-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: C.muted, fontFamily: MONO }}>
          Dashboard — your whole book on one screen
        </span>
        <span className="text-[9px]" style={{ color: "#555", fontFamily: MONO }}>
          Illustrative sample data
        </span>
      </div>
      <div className="relative overflow-hidden mx-4 rounded-md" style={{ border: `1px solid ${C.border}`, background: "#0a0a0a" }}
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        data-testid="dashboard-showcase-frame">
        <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {SLIDES.map(({ key, Comp }) => (
            <div key={key} className="w-full shrink-0" style={{ minHeight: 200 }} data-testid={`dashboard-showcase-slide-${key}`}>
              <Comp C={C} />
            </div>
          ))}
        </div>
        <div className="absolute top-2 right-2 text-[8px] px-1.5 py-0.5 rounded-sm font-bold"
          style={{ background: "rgba(0,0,0,0.65)", color: "#a3a3a3", fontFamily: MONO }}>
          SAMPLE — NOT A LIVE ACCOUNT
        </div>
      </div>
      <div className="px-4 pt-2 flex items-center justify-between flex-wrap gap-2">
        <span className="text-[10px]" style={{ color: C.muted, fontFamily: MONO }} data-testid="dashboard-showcase-slide-label">
          {SLIDES[idx].label}
        </span>
        <div className="flex items-center gap-1.5" data-testid="dashboard-showcase-dots">
          {SLIDES.map((s, i) => (
            <button key={s.key} onClick={() => setIdx(i)} aria-label={`Show slide: ${s.label}`}
              className="rounded-full transition-all"
              style={{ width: i === idx ? 14 : 6, height: 6, background: i === idx ? C.accent : "#333" }}
              data-testid={`dashboard-showcase-dot-${i}`} />
          ))}
        </div>
      </div>
      <div className="px-4 py-2 mt-1 text-[9px] border-t" style={{ color: "#555", borderColor: C.border }} data-testid="dashboard-showcase-caption">
        Sample dashboard, illustrative only — not a live account. Sample data — not a recommendation to buy, sell, or hold any security.
      </div>
    </div>
  );
};
